import './createproject.css';
import { useHistory, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import WebsiteFilter from 'components/shared/WebsiteFilter';
import WebsiteFilterAutocomplete from 'components/shared/WebsiteFilter/WebsiteFilterAutocomplete';
import ProjectSummary from 'components/shared/ProjectSummary';
import QuitProjectCreationModal from 'components/shared/modal/QuitProjectCreationModal';
import ProjectCreatedModal from 'components/shared/modal/ProjectCreatedModal';
import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import { adminClientClientListSelector } from 'store/AdminClient/ClientList';
import { fetchAdminClientClientList } from 'store/AdminClient/ClientList/features';
import axiosPrivate from 'config/axiosPrivate';
import { ORDER_CONTENT_TYPE } from 'utils/Constants';
import { UpDownInputArrows } from 'components/shared/UpDownInputArrows';
import usePageTracking from 'utils/hooks/usePageTracking';

const ADMIN_CLIENT_CREATE_PROJECT_PAGE = '/AdminClient/CreateProject';
const DEFAULT_LLM_TYPE_LIST = [
  { name: 'GPT', type: 'GPT' },
  { name: 'CLAUDE', type: 'CLAUDE' },
  { name: 'CLAUDE_GPT', type: 'CLAUDE_GPT' },
  { name: 'PERPLEXITY', type: 'PERPLEXITY' },
];

const normalizeLlmId = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'both' || normalized === 'claude+gpt' || normalized === 'gpt+claude') {
    return 'claude_gpt';
  }
  return normalized.replace(/\s+/g, '_');
};

const mapLlmTypeOption = (model) => {
  const rawValue = typeof model === 'string'
    ? model
    : (model?.id || model?.value || model?.name || '');
  const id = normalizeLlmId(rawValue);
  if (!id) return null;
  return {
    name: id.toUpperCase(),
    type: id.toUpperCase(),
  };
};

export default function CreateProject() {
  const history = useHistory();
  const location = useLocation();
  const isConsultantRoute = location.pathname.startsWith('/Consultant');
  const projectsPath = isConsultantRoute
    ? '/Consultant/ProjectsEnCours'
    : '/AdminClient/ProjectsEnCours';
  const projectDetailsPath = isConsultantRoute
    ? '/Consultant/DetailsProject'
    : '/AdminClient/DetailsProject';
  usePageTracking(isConsultantRoute ? '/Consultant/CreateProject' : ADMIN_CLIENT_CREATE_PROJECT_PAGE);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [selectedObjective, setSelectedObjective] = useState('redaction');
  const [projectName, setProjectName] = useState('');
  const [siteUrl, setSiteUrl] = useState(() => {
    try {
      return window.localStorage.getItem('AC_Dashboard_SelectedWebsite') || '';
    } catch (e) {
      return '';
    }
  });
  const [language, setLanguage] = useState(null);
  const [availableLanguagesList, setAvailableLanguagesList] = useState([]);
  const [numberOfTexts, setNumberOfTexts] = useState(5);
  const [projectManager, setProjectManager] = useState(null);
  const [projectManagersList, setProjectManagersList] = useState([]);
  const [deadline, setDeadline] = useState('');
  const [summaryCollapsed, setSummaryCollapsed] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [productionMode, setProductionMode] = useState('ia');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [aiAgentsList, setAiAgentsList] = useState([]);
  const [llmType, setLlmType] = useState(null);
  const [llmTypeList, setLlmTypeList] = useState(DEFAULT_LLM_TYPE_LIST);
  const [allLlmTypeList, setAllLlmTypeList] = useState(DEFAULT_LLM_TYPE_LIST);
  const isYoutubeAgent = String(selectedAgent?.typeContenu || '').toUpperCase() === 'YOUTUBE';
  const [selectedWriters, setSelectedWriters] = useState([]);
  const [availableWritersList, setAvailableWritersList] = useState([]);
  const [selectedWriterForDistribution, setSelectedWriterForDistribution] = useState(null);
  const [writerTasks, setWriterTasks] = useState([
    {
      id: Date.now(),
      writer: null,
      numberOfTexts: '',
      deadline: '',
      contentFormat: null,
      customMinWords: '',
      customMaxWords: '',
      modificationLimit: "",
    }
  ]);
  const [contentFormat, setContentFormat] = useState(null);
  const [customWordCount, setCustomWordCount] = useState();
  const [customMinWords, setCustomMinWords] = useState();
  const [customMaxWords, setCustomMaxWords] = useState(8);
  const [contentEnrichment, setContentEnrichment] = useState(false);
  const [ragEnabled, setRagEnabled] = useState(false);
  const [semanticGapLink, setSemanticGapLink] = useState('');
  const [detailedBriefLink, setDetailedBriefLink] = useState('');
  const [showQuitModal, setShowQuitModal] = useState(false);
  const [showProjectCreatedModal, setShowProjectCreatedModal] = useState(false);
  const [createdProjectId, setCreatedProjectId] = useState(null);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showProjectErrorModal, setShowProjectErrorModal] = useState(false);
  const [projectErrorResponse, setProjectErrorResponse] = useState(null);
  const { clients = [] } = useSelector(adminClientClientListSelector) || {};
  const [imageGenerationEnabled, setImageGenerationEnabled] = useState(false);
  const [imageCount, setImageCount] = useState(1);
  const [featureFlags, setFeatureFlags] = useState({
    translationEnabled: true,
    enrichmentEnabled: true,
    ragEnabled: true,
  });
  const isTranslationAvailable = featureFlags.translationEnabled;
  const isEnrichmentAvailable = featureFlags.enrichmentEnabled;
  const isRagAvailable = featureFlags.ragEnabled;
  const contentFormatList = [
    { name: t('Article court - 300 mots'), type: 'SHORT_ARTICLE' },
    { name: t('Article moyen - 600 mots'), type: 'MEDIUM_ARTICLE' },
    { name: t('Article long - 1000 mots'), type: 'LONG_ARTICLE' },
    { name: 'Youtube - 350 mots', type: 'YOUTUBE' },
    { name: 'Linkedin - 200 mots', type: 'LINKEDIN' },
    { name: 'X - 150 mots', type: 'X' },
    { name: t('Personnalisé'), type: 'CUSTOM', isCustom: true },
  ];

  const mapContentTypeToLabel = (typeContenu) => {
    if (!typeContenu) return '';
    const contentTypeItem = ORDER_CONTENT_TYPE.find(item => item.type === typeContenu);
    return contentTypeItem ? contentTypeItem.name : typeContenu;
  };

  // Retourne la liste des rédacteurs disponibles en excluant ceux déjà sélectionnés dans les autres tâches
  const getAvailableWritersForTask = (taskId) => {
    const selectedWriterIds = writerTasks
      .filter(task => task.id !== taskId && task.writer?.id)
      .map(task => task.writer.id);
    return availableWritersList.filter(writer => !selectedWriterIds.includes(writer.id));
  };

  useEffect(() => {
    dispatch(fetchAdminClientClientList());
  }, [dispatch]);

  useEffect(() => {
    try {
      if (siteUrl) {
        window.localStorage.setItem('AC_Dashboard_SelectedWebsite', siteUrl);
        window.dispatchEvent(new CustomEvent('websiteFilterChange_AC_Dashboard_SelectedWebsite', {
          detail: { value: siteUrl }
        }));
      }
    } catch (e) {
    }
  }, [siteUrl]);

  useEffect(() => {
    document.title = "Typix - Créer un projet";
  }, []);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'AC_Dashboard_SelectedWebsite' || e.key === null) {
        try {
          const newValue = window.localStorage.getItem('AC_Dashboard_SelectedWebsite') || '';
          if (newValue !== siteUrl) {
            setSiteUrl(newValue);
          }
        } catch (e) {

        }
      }
    };

    const handleCustomEvent = (e) => {
      if (e.detail && e.detail.value !== undefined) {
        const newValue = e.detail.value || '';
        if (newValue !== siteUrl) {
          setSiteUrl(newValue);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('websiteFilterChange_AC_Dashboard_SelectedWebsite', handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('websiteFilterChange_AC_Dashboard_SelectedWebsite', handleCustomEvent);
    };
  }, [siteUrl]);


  useEffect(() => {
    const hasClients = Array.isArray(clients) && clients.length > 0;

    if (siteUrl && hasClients) {

      const normalizeUrl = (url) => {
        if (!url) return '';
        return url
          .toString()
          .trim()
          .toLowerCase()
          .replace(/^https?:\/\//, '')
          .replace(/^www\./, '')
          .replace(/\/+$/, '');
      };

      const normalizedSiteUrl = normalizeUrl(siteUrl);
      const foundWebsite = clients.find((c) => {
        const url1 = normalizeUrl(c?.urlSite || '');
        const url2 = normalizeUrl(c?.url || '');
        const url3 = normalizeUrl(c?.name || '');
        return url1 === normalizedSiteUrl || url2 === normalizedSiteUrl || url3 === normalizedSiteUrl;
      });
      setSelectedWebsite(foundWebsite || null);
    } else {
      setSelectedWebsite(null);
    }
  }, [siteUrl, clients]);

  useEffect(() => {
    if (selectedWebsite?.language?.id && availableLanguagesList.length > 0) {
      const matchedLanguage = availableLanguagesList.find(
        (lang) => lang.id === selectedWebsite.language.id
      );
      if (matchedLanguage) {
        setLanguage(matchedLanguage);
      }
    } else if (!selectedWebsite) {
      setLanguage(null);
    }
  }, [selectedWebsite, availableLanguagesList]);

  useEffect(() => {
    axiosPrivate.get('/project/language/all')
      .then((res) => setAvailableLanguagesList(res.data || []))
      .catch(() => setAvailableLanguagesList([]));
  }, []);

  useEffect(() => {
    axiosPrivate.get('/user/external/project-managers')
      .then((res) => setProjectManagersList(res.data || []))
      .catch(() => setProjectManagersList([]));
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchCompanyQuotas = async () => {
      try {
        const { data } = await axiosPrivate.get('/project/company-quotas');
        if (!isMounted) return;

        const rawModels = Array.isArray(data?.availableAiModels) ? data.availableAiModels : [];
        const mappedModels = rawModels
          .map(mapLlmTypeOption)
          .filter(Boolean);
        const uniqueModels = Array.from(
          new Map(mappedModels.map((item) => [item.type, item])).values()
        );

        setFeatureFlags({
          translationEnabled: data?.translationEnabled !== false,
          enrichmentEnabled: data?.enrichmentEnabled !== false,
          ragEnabled: data?.ragEnabled !== false,
        });
        const computedLlmTypeList = uniqueModels.length > 0 ? uniqueModels : DEFAULT_LLM_TYPE_LIST;
        setAllLlmTypeList(computedLlmTypeList);
        setLlmTypeList(computedLlmTypeList);
      } catch (error) {
        if (isMounted) {
          setFeatureFlags({
            translationEnabled: true,
            enrichmentEnabled: true,
            ragEnabled: true,
          });
          setAllLlmTypeList(DEFAULT_LLM_TYPE_LIST);
          setLlmTypeList(DEFAULT_LLM_TYPE_LIST);
        }
      }
    };
    fetchCompanyQuotas();
    return () => {
      isMounted = false;
    };
  }, []);

  // Forcer GPT quand l'agent IA est de type YOUTUBE
  useEffect(() => {
    if (!selectedAgent) return;

    const gptOption = { name: 'GPT', type: 'GPT' };

    if (isYoutubeAgent) {
      setLlmTypeList([gptOption]);
      setLlmType(gptOption);
    } else {
      setLlmTypeList(allLlmTypeList);
      setLlmType((prev) => {
        if (prev && allLlmTypeList?.some((opt) => opt?.type === prev?.type)) return prev;
        return allLlmTypeList?.[0] || gptOption;
      });
    }
  }, [selectedAgent, isYoutubeAgent, allLlmTypeList]);

  useEffect(() => {
    if (!isTranslationAvailable && selectedObjective === 'traduction') {
      setSelectedObjective('redaction');
    }
    if (!isEnrichmentAvailable && contentEnrichment) {
      setContentEnrichment(false);
    }
    if (!isRagAvailable && ragEnabled) {
      setRagEnabled(false);
    }
  }, [isTranslationAvailable, isEnrichmentAvailable, isRagAvailable, selectedObjective, contentEnrichment, ragEnabled]);

  useEffect(() => {
    if (productionMode === 'redacteur' || productionMode === 'ia-human') {
      axiosPrivate.get('/user/external/global-writers')
        .then((res) => setAvailableWritersList(res.data || []))
        .catch(() => setAvailableWritersList([]));
      // Réinitialiser l'agent IA si on passe en mode rédacteur
      if (productionMode === 'redacteur') {
        setSelectedAgent(null);
      }
      // Réinitialiser les tâches avec une tâche par défaut
      setWriterTasks([
        {
          id: Date.now(),
          writer: null,
          numberOfTexts: '',
          deadline: '',
          contentFormat: null,
          customMinWords: '',
          customMaxWords: '',
          modificationLimit: "",
        }
      ]);
    } else {
      setAvailableWritersList([]);
      setSelectedWriters([]);
      setSelectedWriterForDistribution(null);
      setWriterTasks([]);
    }
  }, [productionMode]);

  useEffect(() => {
    if (productionMode === 'ia') {
      setSelectedWriters([]);
      setSelectedWriterForDistribution(null);
    }
  }, [productionMode]);

  useEffect(() => {
    if (siteUrl && (productionMode === 'ia' || productionMode === 'ia-human')) {
      const isTranslation = selectedObjective === 'traduction';
      const endpoint = isTranslation
        ? `/smart-content/agent_ia/traduction/all?websiteUrl=${encodeURIComponent(siteUrl)}`
        : `/smart-content/agent_ia/redaction/all?websiteUrl=${encodeURIComponent(siteUrl)}`;

      axiosPrivate.get(endpoint)
        .then((res) => setAiAgentsList(res.data || []))
        .catch(() => setAiAgentsList([]));
    } else {
      setAiAgentsList([]);
    }
  }, [siteUrl, productionMode, selectedObjective]);

  const handleBackClick = () => {
    setShowQuitModal(true);
  };

  const handleQuitWithoutSaving = () => {
    setShowQuitModal(false);
    history.push(projectsPath);
  };

  const handleSaveDraft = () => {
    setShowQuitModal(false);

  };

  const handleViewProject = () => {
    setShowProjectCreatedModal(false);
    if (createdProjectId) {
      try {
        const projectIdString = String(createdProjectId);
        window.localStorage.setItem('AdminClient_ProjectDisplay_id', projectIdString);
        history.push(projectDetailsPath);
      } catch (e) {
        console.error('Erreur lors de la redirection vers le projet:', e);
        history.push(projectsPath);
      }
    } else {
      console.error('Aucun ID de projet disponible pour la redirection');
      history.push(projectsPath);
    }
  };

  const handleStepClick = (targetStep) => {
    if (targetStep < currentStep) {
      setCurrentStep(targetStep);
      return;
    }

    if (targetStep === 2) {
      if (isStep1Valid()) {
        setCurrentStep(2);
      }
    } else if (targetStep === 3) {
      if (productionMode === 'ia-human') {
        if (isStep2Valid()) {
          setCurrentStep(3);
        }
      } else {
        if (isStep2Valid()) {
          setCurrentStep(3);
        }
      }
    } else if (targetStep === 4 && productionMode === 'ia-human') {
      if (isWriterTextsValid()) {
        setCurrentStep(4);
      }
    }
  };

  const addWriterTask = () => {
    setWriterTasks([
      ...writerTasks,
      {
        id: Date.now(),
        writer: null,
        numberOfTexts: '',
        deadline: '',
        contentFormat: null,
        customMinWords: '',
        customMaxWords: '',
        modificationLimit: '',
      }
    ]);
  };

  const removeWriterTask = (taskId) => {
    if (writerTasks.length > 1) {
      setWriterTasks(writerTasks.filter(task => task.id !== taskId));
    }
  };

  const updateWriterTask = (taskId, field, value) => {
    setWriterTasks(writerTasks.map(task =>
      task.id === taskId ? { ...task, [field]: value } : task
    ));
  };

  const isStep1Valid = () => {
    return (
      selectedObjective &&
      projectName &&
      siteUrl &&
      language &&
      numberOfTexts &&
      parseInt(numberOfTexts) > 0 &&
      deadline
    );
  };

  const getTotalWriterTexts = () => {
    if ((productionMode === 'redacteur' || productionMode === 'ia-human') && writerTasks.length > 0) {
      return writerTasks
        .filter(task => task.writer) // Ne compter que les tâches avec un rédacteur
        .reduce((sum, task) => sum + (parseInt(task.numberOfTexts) || 0), 0);
    }
    return 0;
  };

  const isWriterTextsValid = () => {
    if (productionMode === 'redacteur' || productionMode === 'ia-human') {
      const totalWriterTexts = getTotalWriterTexts();
      const maxTexts = parseInt(numberOfTexts) || 0;
      const textsValid = totalWriterTexts <= maxTexts;


      const numberOfTextsValid = writerTasks.every(task => {
        if (!task.writer) return true;
        return task.numberOfTexts && parseInt(task.numberOfTexts) > 0;
      });


      const deadlinesValid = writerTasks.every(task => {
        if (!task.writer) return true;
        return task.deadline && task.deadline.trim() !== '';
      });


      const formatsValid = productionMode === 'redacteur'
        ? writerTasks.every(task => {
          if (!task.writer) return true;
          return task.contentFormat !== null;
        })
        : true;


      const customFormatsValid = writerTasks.every(task => {
        if (!task.writer) return true;
        if (task.contentFormat?.isCustom) {
          const hasValidMaxWords = task.customMaxWords && parseInt(task.customMaxWords) > 0;
          const hasValidMinWords = task.customMinWords && parseInt(task.customMinWords) > 0;
          return hasValidMaxWords && hasValidMinWords;
        }
        return true;
      });

      return textsValid && numberOfTextsValid && deadlinesValid && formatsValid && customFormatsValid;
    }
    return true;
  };

  const isAllTextsDistributed = () => {
    if (productionMode === 'redacteur' || productionMode === 'ia-human') {
      const totalWriterTexts = getTotalWriterTexts();
      const maxTexts = parseInt(numberOfTexts) || 0;
      return maxTexts > 0 && totalWriterTexts > maxTexts;
    }
    return false;
  };

  const isMaxTextsReached = () => {
    if (productionMode === 'redacteur' || productionMode === 'ia-human') {
      const totalWriterTexts = getTotalWriterTexts();
      const maxTexts = parseInt(numberOfTexts) || 0;
      return maxTexts > 0 && totalWriterTexts >= maxTexts;
    }
    return false;
  };

  const isStep2Valid = () => {
    const hasFormat = !!contentFormat;
    const hasAgent = !!selectedAgent && !!siteUrl;
    // Nombre de mots obligatoire pour format personnalisé
    const hasValidCustomWordCount = contentFormat?.isCustom
      ? (customWordCount && parseInt(customWordCount) > 0)
      : true;

    if (productionMode === 'ia') {
      return hasFormat && hasAgent && hasValidCustomWordCount;
    }

    if (productionMode === 'ia-human') {
      return hasFormat && hasAgent && hasValidCustomWordCount;
    }

    // Mode rédacteur : vérifier que le total des textes ne dépasse pas le nombre de la section 1
    if (productionMode === 'redacteur') {
      return !!productionMode && isWriterTextsValid();
    }

    return !!productionMode && hasFormat && hasValidCustomWordCount;
  };

  const formatDateFrench = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const months = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const handleLaunchProject = async () => {
    try {
      const getMaxWords = () => {
        if (productionMode === 'redacteur') {
          const firstTaskWithWriter = writerTasks.find(task => task.writer && task.contentFormat);
          if (firstTaskWithWriter) {
            if (firstTaskWithWriter.contentFormat?.isCustom) {
              if (firstTaskWithWriter.customMaxWords && firstTaskWithWriter.customMaxWords !== '') {
                const parsedMax = parseInt(firstTaskWithWriter.customMaxWords);
                return !isNaN(parsedMax) ? parsedMax : '';
              }
              return '';
            } else {
              switch (firstTaskWithWriter.contentFormat?.type) {
                case 'SHORT_ARTICLE': return 300;
                case 'MEDIUM_ARTICLE': return 600;
                case 'LONG_ARTICLE': return 1000;
                case 'YOUTUBE': return 350;
                case 'LINKEDIN': return 200;
                case 'X': return 150;
                default: return '';
              }
            }
          }
          return '';
        }

        // Pour les modes ia et ia-human, utiliser le format global
        if (contentFormat?.isCustom) {
          return parseInt(customWordCount) || 850;
        }
        switch (contentFormat?.type) {
          case 'SHORT_ARTICLE':
            return 300;
          case 'MEDIUM_ARTICLE':
            return 600;
          case 'LONG_ARTICLE':
            return 1000;
          case 'YOUTUBE':
            return 350;
          case 'LINKEDIN':
            return 200;
          case 'X':
            return 150;
          default:
            return 850;
        }
      };

      const getType = () => {
        switch (productionMode) {
          case 'ia':
            return 'SMART';
          case 'redacteur':
            return 'PREMIUM';
          case 'ia-human':
            return 'SMART_WITH_REVIEW';
          default:
            return 'SMART';
        }
      };


      const getTextType = () => {
        return selectedObjective === 'traduction' ? 'TRANSLATION' : 'EDITORIAL';
      };

      const getContentType = () => {
        if (contentEnrichment && isEnrichmentAvailable) {
          return 'OPTIMIZE';
        }
        return '';
      };

      const formatDeadline = () => {
        if (!deadline) {
          return '';
        }
        return `${deadline}T23:59:59`;
      };

      const getWriterAttributed = () => {
        if ((productionMode === 'redacteur' || productionMode === 'ia-human') && writerTasks.length > 0) {
          return writerTasks
            .filter(task => task.writer)
            .map(task => {
              let taskMaxWords;
              let taskMinWords;

              if (productionMode === 'ia-human') {
                if (task.modificationLimit !== undefined && task.modificationLimit !== null && task.modificationLimit !== '') {
                  const parsedLimit = parseInt(String(task.modificationLimit).trim());
                  taskMaxWords = !isNaN(parsedLimit) && parsedLimit > 0 ? parsedLimit : 850;
                } else {
                  taskMaxWords = 850;
                }
                taskMinWords = taskMaxWords;
              } else if (task.contentFormat?.isCustom) {
                const parsedMax = task.customMaxWords && task.customMaxWords !== ''
                  ? parseInt(task.customMaxWords)
                  : null;
                const parsedMin = task.customMinWords && task.customMinWords !== ''
                  ? parseInt(task.customMinWords)
                  : null;

                taskMaxWords = parsedMax && !isNaN(parsedMax) ? parsedMax : 850;
                taskMinWords = parsedMin && !isNaN(parsedMin) ? parsedMin : taskMaxWords;
              } else if (task.contentFormat?.type) {
                // Pour le mode rédacteur avec format prédéfini
                switch (task.contentFormat.type) {
                  case 'SHORT_ARTICLE':
                    taskMaxWords = 300;
                    break;
                  case 'MEDIUM_ARTICLE':
                    taskMaxWords = 600;
                    break;
                  case 'LONG_ARTICLE':
                    taskMaxWords = 1000;
                    break;
                  case 'YOUTUBE':
                    taskMaxWords = 350;
                    break;
                  case 'LINKEDIN':
                    taskMaxWords = 200;
                    break;
                  case 'X':
                    taskMaxWords = 150;
                    break;
                  default:
                    taskMaxWords = 850;
                }
                taskMinWords = taskMaxWords;
              } else {
                taskMaxWords = 850;
                taskMinWords = 850;
              }

              return {
                writerId: task.writer?.id || task.writer,
                deadline: task.deadline ? `${task.deadline}T23:59:59` : null,
                textAmount: task.numberOfTexts || 1,
                maxWords: taskMaxWords || 850,
                minWords: taskMinWords || 850,
                wordsManagedNumber: 0,
              };
            });
        }
        return [];
      };

      const maxWordsValue = getMaxWords();
      const body = {
        title: projectName,
        textAmount: parseInt(numberOfTexts) || 5,
        ...(maxWordsValue !== '' && { maxWords: maxWordsValue || 850 }),
        projectManagerId: projectManager?.id || 0,
        aiAgentId: selectedAgent?.id || selectedAgent?.agentId || '',
        aiAgentName: selectedAgent?.name || selectedAgent?.agentName || '',
        llmType: isYoutubeAgent ? 'GPT' : (llmType?.type || ''),
        urlWebsite: siteUrl || '',
        languageId: language?.id || 0,
        thematicIds: [],
        type: getType(),
        textType: getTextType(),
        contentType: getContentType(),
        detailedBrief: detailedBriefLink || '',
        semanticGap: semanticGapLink || '',
        deadline: formatDeadline(),
        writerAttributed: getWriterAttributed(),
        ragEnabled: ragEnabled && isRagAvailable,
        hasImages: imageGenerationEnabled,
        numberOfImages: imageGenerationEnabled ? imageCount : 0,
      };

      const response = await axiosPrivate.post('/project', body);
      const projectData = response.data;
      console.log(projectData.hasImages);
      console.log(projectData.numberOfImages);
      if (response?.data?.id) {
        const projectId = response.data.id;
        setCreatedProjectId(projectId);
        setShowProjectCreatedModal(true);
      } else {
        console.error('Impossible de récupérer l\'ID du projet depuis la réponse:', response?.data);
        alert('Projet créé mais impossible de récupérer l\'ID. Redirection vers la liste des projets.');
        history.push(projectsPath);
      }
    } catch (error) {
      console.error('Erreur lors de la création du projet:', error);

      if (error.response?.status === 406 || error.response?.status === 400) {
        setProjectErrorResponse(error.response);
        setShowProjectErrorModal(true);
      } else {
        setErrorMessage(t('Erreur lors de la création du projet. Veuillez réessayer.'));
        setShowErrorModal(true);
      }
    }
  };

  const totalStep1Fields = 7;
  const filledStep1Fields = [
    selectedObjective,
    projectName,
    siteUrl,
    language,
    numberOfTexts && parseInt(numberOfTexts, 10) > 0,
    projectManager,
    deadline,
  ].filter(Boolean).length;
  const step1Progress = Math.min(1, filledStep1Fields / totalStep1Fields);

  const totalStep2Fields = 3;
  const filledStep2Fields = [
    productionMode,
    contentFormat,
    selectedAgent && siteUrl,
  ].filter(Boolean).length;
  const step2Progress = Math.min(1, filledStep2Fields / totalStep2Fields);

  const writerAssignmentStarted = writerTasks.some(
    (task) => task.writer || task.numberOfTexts || task.deadline || task.contentFormat
  );

  const projectStepperTabs = productionMode === 'ia-human'
    ? [
      {
        id: 1,
        label: t('Objectif du projet'),
        targetStep: 1,
        canNavigate: currentStep > 1,
        progress: currentStep > 1 ? 1 : step1Progress,
      },
      {
        id: 2,
        label: t('Mode de production'),
        targetStep: 2,
        canNavigate: currentStep > 2 || isStep1Valid(),
        progress: currentStep > 2 ? 1 : step2Progress,
      },
      {
        id: 3,
        label: t('Répartition du travail'),
        targetStep: 3,
        canNavigate: currentStep > 3 || isStep2Valid(),
        progress: currentStep > 3 ? 1 : (writerAssignmentStarted ? 0.45 : 0),
      },
      {
        id: 4,
        label: t('Contexte éditorial'),
        targetStep: 4,
        canNavigate: currentStep > 4 || isWriterTextsValid(),
        progress: currentStep > 4 ? 1 : (currentStep === 4 ? 0.45 : 0),
      },
    ]
    : [
      {
        id: 1,
        label: t('Objectif du projet'),
        targetStep: 1,
        canNavigate: currentStep > 1,
        progress: currentStep > 1 ? 1 : step1Progress,
      },
      {
        id: 2,
        label: t('Mode de production'),
        targetStep: 2,
        canNavigate: currentStep > 2 || isStep1Valid(),
        progress: currentStep > 2 ? 1 : step2Progress,
      },
      {
        id: 3,
        label: t('Contexte éditorial'),
        targetStep: 3,
        canNavigate: currentStep > 3 || isStep2Valid(),
        progress: currentStep > 3 ? 1 : (currentStep === 3 ? 0.45 : 0),
      },
    ];

  return (
    <div className="create-project-container">
      <div className="create-project-content">
        <div className="create-project-header">
          <div className="header-left">
            <button className="back-button" onClick={handleBackClick}>
              <img src="/Images/icon-arrow-project.png" alt="back" className="back-arrow" />
              <span>{t('Quitter la création')}</span>
            </button>
          </div>
          <div className="header-right">
            <div className="project-steps-nav">
              {projectStepperTabs.map((tab, idx) => {
                const isFuture = tab.id > currentStep;
                const progress = tab.progress || 0;
                const visualProgress = isFuture ? 0 : progress;
                const isCompleted = !isFuture && visualProgress >= 1;
                const isPartial = !isFuture && visualProgress > 0 && visualProgress < 1;
                const isActive = currentStep === tab.id;
                const isLast = idx === projectStepperTabs.length - 1;
                const isClickable = tab.canNavigate || isActive;
                return (
                  <div
                    key={tab.id}
                    className={`project-step-btn${isActive ? ' active' : ''}${isCompleted ? ' completed' : ''}${isFuture ? ' future' : ''}`}
                  >
                    <div className="project-step-visual">
                      <span
                        className={`project-step-index${isCompleted ? ' completed' : ''}${isPartial ? ' partial' : ''}`}
                        style={{ '--step-progress': `${Math.round(visualProgress * 100)}%`, cursor: isClickable ? 'pointer' : 'default' }}
                        role={isClickable ? 'button' : undefined}
                        tabIndex={isClickable ? 0 : -1}
                        onClick={() => {
                          if (isClickable) handleStepClick(tab.targetStep);
                        }}
                        onKeyDown={(e) => {
                          if (!isClickable) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleStepClick(tab.targetStep);
                          }
                        }}
                        aria-label={`${t('Aller à l’étape')} ${tab.id}: ${tab.label}`}
                      >
                        {isCompleted ? '✓' : tab.id}
                      </span>
                      {!isLast && (
                        <>
                          <span className="project-step-connector before-chevron" aria-hidden="true" />
                          <span className="project-step-chevron" aria-hidden="true">›</span>
                          <span className="project-step-connector after-chevron" aria-hidden="true" />
                        </>
                      )}
                    </div>
                    <span className="project-step-label">{tab.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className={`create-project-body-wrapper ${summaryCollapsed && ((currentStep === 3 && productionMode !== 'ia-human') || (currentStep === 4 && productionMode === 'ia-human')) ? 'summary-collapsed-step3' : ''}`}>
          <div className="create-project-body">
            {currentStep === 1 && (
              <>
                <h1 className="project-title">{t('Objectif du projet')}</h1>
                <div className="objective-cards">
                  <div
                    className={`objective-card ${selectedObjective === 'redaction' ? 'selected' : ''}`}
                    onClick={() => setSelectedObjective('redaction')}
                  >
                    <div className="card-check">
                      {selectedObjective === 'redaction' ? (
                        <div className="checkmark-circle">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      ) : (
                        <div className="empty-circle"></div>
                      )}
                    </div>
                    <div className="card-content">
                      <div className="card-title-wrapper">
                        <img src="/Images/red-icon.png" alt="Rédaction" className="card-title-icon" />
                        <h3 className="card-title">{t('Rédaction')}</h3>
                      </div>
                      <p className="card-description">{t('Créer du contenu original')}</p>
                    </div>
                  </div>
                  <div
                    className={`objective-card ${selectedObjective === 'traduction' ? 'selected' : ''} ${!isTranslationAvailable ? 'objective-card-disabled' : ''}`}
                    onClick={() => {
                      if (isTranslationAvailable) {
                        setSelectedObjective('traduction');
                      }
                    }}
                  >
                    <div className="card-check">
                      {selectedObjective === 'traduction' ? (
                        <div className="checkmark-circle">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      ) : (
                        <div className="empty-circle"></div>
                      )}
                    </div>
                    <div className="card-content">
                      <div className="card-title-wrapper">
                        <img src="/Images/trad-icon.png" alt="Traduction" className="card-title-icon" />
                        <h3 className="card-title">{t('Traduction')}</h3>
                      </div>
                      <p className="card-description">{t('Traduire un contenu existant')}</p>
                    </div>
                  </div>
                </div>
                <div className="separator-line"></div>
                <h2 className="identification-title">{t('Identification du projet')}</h2>
                <div className="project-identification">

                  <div className="form-fields">
                    <div className="form-field form-field-project-name">
                      <div className="field-label-wrapper">
                        <label className="field-label field-label-required">{t('Nom du projet')}</label>
                        <span className="field-hint">({t('Vous pourrez modifier le nom plus tard')})</span>
                      </div>
                      <input
                        type="text"
                        className={`form-input ${!projectName ? 'field-required-empty' : ''}`}
                        value={projectName}
                        onChange={(e) => setProjectName(e.target.value)}
                      />
                    </div>
                    <div className="form-field form-field-bottom-border">
                      <label className="field-label">{t('URL du site')}</label>
                      <div className="form-autocomplete-wrapper form-autocomplete-wrapper-site">
                        <WebsiteFilter
                          websitesList={clients}
                          selectedWebsiteUrl={siteUrl}
                          setSelectedWebsiteUrl={setSiteUrl}
                          storageKey="AC_Dashboard_SelectedWebsite"
                          style={{ maxWidth: 300, width: 300 }}
                        />
                      </div>
                    </div>
                    <div className="form-field">
                      <label className="field-label">{t('Langue')}</label>
                      <div className="form-autocomplete-wrapper">
                        <WebsiteFilterAutocomplete
                          disabled={true}
                          list={availableLanguagesList}
                          value={language}
                          setValue={(option) => setLanguage(option)}
                          getOptionLabel={(option) => option?.language || ''}
                          placeholder={t('Sélectionner une langue')}
                          style={{ maxWidth: 300, width: 300 }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="number-of-texts-card">
                  <div className="number-of-texts-content">
                    <div className="number-of-texts-label-wrapper">
                      <label className="number-of-texts-label field-label-required">{t('Nombre de textes')}</label>

                    </div>
                    <input
                      type="number"
                      className={`number-of-texts-input ${!numberOfTexts || parseInt(numberOfTexts) <= 0 ? 'field-required-empty' : ''}`}
                      value={numberOfTexts}
                      onChange={(e) => setNumberOfTexts(e.target.value)}
                      min="1"
                    />
                  </div>
                </div>
                <div className="project-manager-deadline-block">
                  <div className="form-fields">
                    <div className="form-field form-field-bottom-border">
                      <label className="field-label">{t('Chef de projet')}</label>
                      <div className="form-autocomplete-wrapper">
                        <WebsiteFilterAutocomplete
                          disabled={projectManagersList.length <= 0}
                          list={projectManagersList}
                          value={projectManager}
                          setValue={(option) => setProjectManager(option)}
                          getOptionLabel={(option) =>
                            option?.mail ||
                            [option?.firstName, option?.lastName].filter(Boolean).join(' ') ||
                            ''
                          }
                          placeholder={
                            projectManagersList.length === 0
                              ? t("Aucun chef de projet assigné au siteweb")
                              : t("Sélectionner un chef de projet")
                          }
                          style={{ maxWidth: 300, width: 300 }}
                        />
                      </div>
                    </div>
                    <div className="form-field">
                      <label className="field-label field-label-required">{t('Date limite du projet')}</label>
                      <div
                        className={`date-input-wrapper ${!deadline ? 'date-empty' : ''}`}
                        onClick={(e) => {
                          const input = document.getElementById('project-deadline-input');
                          if (input && e.target !== input) {
                            e.preventDefault();
                            input.focus();
                            input.click();
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <input
                          type="date"
                          id="project-deadline-input"
                          className={`date-input ${!deadline ? 'field-required-empty' : ''}`}
                          value={deadline}
                          onChange={(e) => setDeadline(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          required
                        />
                        {!deadline && (
                          <span className="date-placeholder">{t('Sélectionner une date limite')}</span>
                        )}
                        <div className="date-icon-wrapper" onClick={(e) => {
                          e.stopPropagation();
                          const input = document.getElementById('project-deadline-input');
                          if (input) {
                            input.focus();
                            input.showPicker?.();
                          }
                        }}>
                          <img
                            src="/Images/icon-calendar.png"
                            alt="calendar"
                            className="date-icon"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="next-button-wrapper">
                  <button
                    className="next-button"
                    disabled={!isStep1Valid()}
                    onClick={() => {
                      if (isStep1Valid()) {
                        setCurrentStep(2);
                      }
                    }}
                  >
                    <span>{t("Suivant")}</span>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 12L10 8L6 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              </>
            )}
            {currentStep === 2 && (
              <div>
                <h1 className="project-title">{t('Mode de production')}</h1>
                <p className="step-description">{t('Définissez l\'objectif et le cadre général de votre projet.')}</p>
                <div className="production-mode-cards">
                  <div
                    className={`production-mode-card ${productionMode === 'ia' ? 'selected' : ''}`}
                    onClick={() => setProductionMode('ia')}
                  >
                    <div className="card-content">
                      <div className="card-title-wrapper">
                        <div className="icon-check-wrapper">
                          {productionMode === 'ia' ? (
                            <div className="checkmark-circle">
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                          ) : (
                            <div className="empty-circle"></div>
                          )}
                          <img src="/Images/ia-icon-proj.png" alt="IA" className="production-icon" />
                        </div>
                        <h3 className="card-title">{t('IA')}</h3>
                      </div>
                      <p className="card-description">{t('Production généré par IA')}</p>
                    </div>
                  </div>
                  <div
                    className={`production-mode-card ${productionMode === 'redacteur' ? 'selected' : ''}`}
                    onClick={() => setProductionMode('redacteur')}
                  >
                    <div className="card-content">
                      <div className="card-title-wrapper">
                        <div className="icon-check-wrapper">
                          {productionMode === 'redacteur' ? (
                            <div className="checkmark-circle">
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                          ) : (
                            <div className="empty-circle"></div>
                          )}
                          <img src="/Images/redac-icon.png" alt="Rédacteur" className="production-icon" />
                        </div>
                        <h3 className="card-title">{t('Rédacteur')}</h3>
                      </div>
                      <p className="card-description">{t('Production par un rédacteur')}</p>
                    </div>
                  </div>
                  <div
                    className={`production-mode-card ${productionMode === 'ia-human' ? 'selected' : ''}`}
                    onClick={() => setProductionMode('ia-human')}
                  >
                    <div className="card-content">
                      <div className="card-title-wrapper">
                        <div className="icon-check-wrapper">
                          {productionMode === 'ia-human' ? (
                            <div className="checkmark-circle">
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                          ) : (
                            <div className="empty-circle"></div>
                          )}
                          <img src="/Images/smart-with-review-icon.png" alt="IA + revue humaine" className="production-icon" />
                        </div>
                        <h3 className="card-title">{t('IA + revue humaine')}</h3>
                      </div>
                      <p className="card-description">{t('IA avec validation humaine.')}</p>
                    </div>
                  </div>
                </div>

                <div className="separator-line separator-line-production"></div>
                {productionMode === 'redacteur' && (
                  <>
                    <h2
                      className="agent-ia-title"
                      style={{ marginTop: '32px', marginBottom: '12px', fontSize: '16px' }}
                    >
                      {t('Répartition du travail')}
                    </h2>
                    <p className="writer-section-description">
                      {t('Répartissez les textes de votre projet entre vous et d\'éventuels rédacteurs.')}
                      <br />
                      {t('L\'attribution à un rédacteur est optionnelle.')}
                    </p>
                    {productionMode === 'redacteur' && writerTasks.length > 0 && (
                      <div className="writer-section-counter-block">
                        <img
                          src="/Images/icons/verif-icon.png"
                          alt="verification"
                          className="writer-section-counter-icon"
                        />
                        <span className="writer-section-counter">
                          {!isWriterTextsValid()
                            ? `${getTotalWriterTexts()} / ${numberOfTexts} ${t('textes répartis')}`
                            : `${getTotalWriterTexts()} / ${numberOfTexts} ${t('textes répartis')}`}
                        </span>
                      </div>
                    )}

                    {writerTasks.map((task, index) => (
                      <div
                        className="writer-task-card"
                        key={task.id}
                        style={{
                          marginLeft: '140px',
                          marginRight: 'auto',
                          maxWidth: '940px',
                          padding: '20px 24px',
                          borderRadius: '12px',
                          border: '1px solid #E5E7EB',
                          boxShadow: '0 4px 10px rgba(15, 23, 42, 0.04)',
                          backgroundColor: '#FFFFFF',
                          fontFamily: "'Poppins', sans-serif",
                          marginBottom: index < writerTasks.length - 1 ? '18px' : '0',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '24px',
                            marginBottom: '18px',
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <label
                              style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: 600,
                                marginBottom: '6px',
                              }}
                            >
                              {t('Rédacteur (optionnel)')}
                            </label>
                            <WebsiteFilterAutocomplete
                              disabled={availableWritersList.length <= 0}
                              list={getAvailableWritersForTask(task.id)}
                              value={task.writer}
                              setValue={(option) => updateWriterTask(task.id, 'writer', option)}
                              getOptionLabel={(option) =>
                                option?.mail ||
                                [option?.firstName, option?.lastName].filter(Boolean).join(' ') ||
                                option?.name ||
                                ''
                              }
                              placeholder={
                                availableWritersList.length === 0
                                  ? t('Aucun rédacteur disponible')
                                  : t("Sélectionner un rédacteur")
                              }
                              style={{ width: '100%' }}
                            />
                            {isAllTextsDistributed() && (
                              <div style={{
                                fontSize: '12px',
                                color: '#D97706',
                                marginTop: '4px',
                              }}>
                                <div>{t("Nombre de textes maximum atteint.")}</div>
                                <div>{t('Vous avez réparti tous les textes disponibles pour ce projet.')}</div>
                              </div>
                            )}
                          </div>

                          <div style={{ width: '150px' }}>
                            <label
                              style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: 500,
                                marginBottom: '6px',
                              }}
                            >
                              {t('Nombre de textes')}
                            </label>
                            <input
                              type="number"
                              value={task.numberOfTexts || ''}
                              disabled={!task.writer}
                              onChange={(e) => {
                                const value = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                                updateWriterTask(task.id, 'numberOfTexts', value);
                              }}
                              min="1"
                              max={parseInt(numberOfTexts) || 999}
                              required={!!task.writer}
                              style={{
                                width: '100%',
                                height: '40px',
                                borderRadius: '8px',
                                border: task.writer && (!task.numberOfTexts || parseInt(task.numberOfTexts) <= 0) ? '1px solid #8B5CF6' : '1px solid #E5E7EB',
                                padding: '0 12px',
                                fontSize: '14px',
                                color: '#111827',
                                backgroundColor: '#FFFFFF',
                              }}
                            />
                          </div>

                          <div style={{ width: '190px' }}>
                            <label
                              style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: 500,
                                marginBottom: '6px',
                              }}
                            >
                              Deadline
                            </label>
                            <div
                              className={`date-input-wrapper ${!task.deadline ? 'date-empty' : ''}`}
                              style={{ cursor: task.writer ? 'pointer' : 'not-allowed', width: '100%', maxWidth: '100%', fontSize: '12px' }}
                              onClick={(e) => {
                                if (!task.writer) return;
                                const input = e.currentTarget.querySelector('input[type="date"]');
                                if (input && e.target !== input) {
                                  e.preventDefault();
                                  input.focus();
                                  input.click();
                                }
                              }}
                            >
                              <input
                                type="date"
                                className={`date-input ${task.writer && !task.deadline ? 'field-required-empty' : ''}`}
                                disabled={!task.writer}
                                value={task.deadline}
                                onChange={(e) => updateWriterTask(task.id, 'deadline', e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                                required={!!task.writer}
                                style={{
                                  fontSize: '12px',
                                  color: task.deadline ? '#111827' : 'transparent'
                                }}
                              />
                              {!task.deadline && (
                                <span className="date-placeholder" style={{ fontSize: '12px' }}>{t('Sélectionner une date limite')}</span>
                              )}
                              <div className="date-icon-wrapper" onClick={(e) => {
                                e.stopPropagation();
                                const input = e.currentTarget.parentElement.querySelector('input[type="date"]');
                                if (input) {
                                  input.focus();
                                  input.showPicker?.();
                                }
                              }}>
                                <img
                                  src="/Images/icon-calendar.png"
                                  alt="calendar"
                                  className="date-icon"
                                  style={{ width: '14px', height: '14px' }}
                                />
                              </div>
                            </div>
                          </div>

                          {writerTasks.length > 1 && (
                            <button
                              type="button"
                              aria-label={t("Supprimer la ligne")}
                              onClick={() => removeWriterTask(task.id)}
                              style={{
                                marginLeft: 'auto',
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#F1F1F1',
                                borderRadius: '10%',
                                marginTop: '-10px',
                              }}
                            >
                              <img
                                src="Images/icon-trash.png"
                                alt="icon-trash"
                                style={{ width: '25px', height: '25px', opacity: 0.85 }}
                              />
                            </button>
                          )}
                        </div>

                        <hr
                          style={{
                            border: 'none',
                            borderTop: '1px solid #B0B0B0',
                            margin: '16px 0 22px',
                          }}
                        />

                        <div>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '20px',
                            }}
                          >
                            <div className="writer-content-format-title">
                              {t('Format du contenu')}
                            </div>
                            <div style={{ width: '360px' }}>
                              <WebsiteFilterAutocomplete
                                disabled={!task.writer}
                                list={contentFormatList}
                                value={task.contentFormat}
                                setValue={(option) => updateWriterTask(task.id, 'contentFormat', option)}
                                getOptionLabel={(option) => option?.name || ''}
                                placeholder={task.writer ? t("Sélectionner un format") : t("Sélectionner d'abord un rédacteur")}
                                style={{
                                  maxWidth: 400,
                                  width: '100%',
                                  backgroundImage: task.contentFormat?.isCustom
                                    ? "url('/Images/perso-icon.png')"
                                    : 'none',
                                  backgroundRepeat: 'no-repeat',
                                  backgroundPosition: task.contentFormat?.isCustom
                                    ? '10px center'
                                    : 'initial',
                                  backgroundSize: task.contentFormat?.isCustom
                                    ? '16px 16px'
                                    : 'initial',
                                  paddingLeft: task.contentFormat?.isCustom ? '32px' : '10px',
                                  border: task.writer && !task.contentFormat ? '1px solid #8B5CF6' : '1px solid #e0e0e0',
                                }}
                                renderOptionLabel={(option) => {
                                  if (option?.isCustom) {
                                    return (
                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px',
                                          width: '100%',
                                        }}
                                      >
                                        <img
                                          src="/Images/perso-icon.png"
                                          alt="Personnalisé"
                                          style={{ width: '16px', height: '16px' }}
                                        />
                                        <span>{option.name}</span>
                                      </div>
                                    );
                                  }
                                  return option?.name || '';
                                }}
                              />
                            </div>
                          </div>

                          {task.contentFormat?.isCustom && (
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'flex-start',
                                gap: '12px',
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    marginBottom: '4px',
                                  }}
                                >
                                  {t('Nombre de mots par texte')}
                                </div>
                                <div
                                  style={{
                                    fontSize: '12px',
                                    color: '#6B7280',
                                  }}
                                >
                                  {t('Format personnalisé - définir le nombre de mots')}
                                </div>
                              </div>

                              <div
                                style={{
                                  display: 'flex',
                                  gap: '12px',
                                  width: '360px',
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <div
                                      style={{
                                        padding: '0 10px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        border: '1px solid #E5E7EB',
                                        borderRight: 'none',
                                        borderRadius: '8px 0 0 8px',
                                        fontSize: '12px',
                                        color: '#6B7280',
                                        backgroundColor: '#F9FAFB',
                                      }}
                                    >
                                      Min
                                    </div>
                                    <input
                                      type="number"
                                      value={task.customMinWords}
                                      disabled={!task.writer}
                                      onChange={(e) => updateWriterTask(task.id, 'customMinWords', e.target.value)}
                                      required
                                      style={{
                                        width: '100%',
                                        height: '40px',
                                        borderRadius: '0 8px 8px 0',
                                        border: '1px solid #E5E7EB',
                                        padding: '0 12px',
                                        fontSize: '14px',
                                        borderLeft: 'none',
                                      }}
                                    />
                                  </div>
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <div
                                      style={{
                                        padding: '0 10px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        border: '1px solid #E5E7EB',
                                        borderRight: 'none',
                                        borderRadius: '8px 0 0 8px',
                                        fontSize: '12px',
                                        color: '#6B7280',
                                        backgroundColor: '#F9FAFB',
                                      }}
                                    >
                                      Max
                                    </div>
                                    <input
                                      type="number"
                                      value={task.customMaxWords}
                                      disabled={!task.writer}
                                      onChange={(e) => updateWriterTask(task.id, 'customMaxWords', e.target.value)}
                                      required
                                      style={{
                                        width: '100%',
                                        height: '40px',
                                        borderRadius: '0 8px 8px 0',
                                        border: '1px solid #E5E7EB',
                                        padding: '0 12px',
                                        fontSize: '14px',
                                        borderLeft: 'none',
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    <button
                      className="add-task-button-block"
                      type="button"
                      onClick={addWriterTask}
                      disabled={isMaxTextsReached()}
                      style={{
                        width: '940px',
                        marginLeft: '140px',
                        marginTop: '18px',
                        padding: '12px 24px',
                        border: '2px dashed #CFCFCF',
                        borderRadius: '12px',
                        backgroundColor: isMaxTextsReached() ? '#F5F5F5' : '#FFFFFF',
                        cursor: isMaxTextsReached() ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '14px',
                        fontWeight: 500,
                        color: isMaxTextsReached() ? '#9CA3AF' : '#1A1A1A',
                        transition: 'all 0.2s ease',
                        opacity: isMaxTextsReached() ? 0.6 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (!isMaxTextsReached()) {
                          e.target.style.borderColor = '#9CA3AF';
                          e.target.style.backgroundColor = '#F9FAFB';
                          e.target.style.color = '#1A1A1A';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isMaxTextsReached()) {
                          e.target.style.borderColor = '#D1D5DB';
                          e.target.style.backgroundColor = '#FFFFFF';
                          e.target.style.color = '#1A1A1A';
                        }
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 3V13M3 8H13"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="add-task-button-label">{t('Ajouter une tâche')}</span>
                    </button>
                  </>
                )}
                {productionMode === 'ia' || productionMode === 'ia-human' ? (
                  <>
                    <h2 className="agent-ia-title">{t('Agent IA')}</h2>
                    <div className="agent-ia-block">
                      <div className="form-fields">
                        <div className="form-field">
                          <div className="field-label-wrapper">
                            <label className="field-label field-label-required">{t('Agent IA')}</label>
                            <span className="field-hint">({t('Vous pourrez changer d\'agent plus tard')})</span>
                          </div>
                          <div className={`form-autocomplete-wrapper ${!selectedAgent && (productionMode === 'ia' || productionMode === 'ia-human') ? 'field-required-empty' : ''}`}>
                            <WebsiteFilterAutocomplete
                              disabled={aiAgentsList.length <= 0 || !siteUrl || (productionMode !== 'ia' && productionMode !== 'ia-human')}
                              list={aiAgentsList}
                              value={selectedAgent}
                              setValue={(option) => setSelectedAgent(option)}
                              getOptionLabel={(option) => option?.name || option?.agentName || ''}
                              renderOptionLabel={(option) => {
                                const agentName = option?.name || option?.agentName || '';
                                const typeContenu = option?.typeContenu || '';
                                const typeLabel = mapContentTypeToLabel(typeContenu);

                                if (!typeLabel) {
                                  return agentName;
                                }

                                return (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>{agentName}</span>
                                    <span style={{
                                      fontSize: '11px',
                                      color: '#9CA3AF',
                                      fontWeight: 400
                                    }}>
                                      {typeLabel}
                                    </span>
                                  </span>
                                );
                              }}
                              placeholder={
                                !siteUrl
                                  ? t("Veuillez d'abord sélectionner un site web")
                                  : (productionMode !== 'ia' && productionMode !== 'ia-human')
                                    ? t("Sélectionnez le mode IA")
                                    : aiAgentsList.length === 0
                                      ? t("Aucun agent disponible")
                                      : t("Sélectionner un agent IA")
                              }
                              style={{
                                maxWidth: 300,
                                width: 300,
                                border: !selectedAgent && (productionMode === 'ia' || productionMode === 'ia-human') ? '1px solid #8B5CF6' : '1px solid #e0e0e0'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <h2 className="agent-ia-title">{t('Moteur LLM')}</h2>
                    <div className="agent-ia-block">
                      <div className="form-fields">
                        <div className="form-field">
                          <div className="field-label-wrapper">
                            <label className="field-label">{t('Moteur LLM')}</label>
                          </div>
                          <div className="form-autocomplete-wrapper">
                            <WebsiteFilterAutocomplete
                              disabled={isYoutubeAgent}
                              list={llmTypeList}
                              value={llmType}
                              setValue={(option) => setLlmType(option)}
                              getOptionLabel={(option) => option?.name || ''}
                              placeholder={t("Sélectionner un moteur LLM")}
                              style={{
                                maxWidth: 300,
                                width: 300,
                                border: '1px solid #e0e0e0'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <h2 className="content-format-title">{t('Format du contenu')}</h2>
                    <div className="content-format-block">
                      <div className="form-fields">
                        <div className={`form-field ${contentFormat?.isCustom ? 'form-field-bottom-border' : ''}`}>
                          <div className="field-label-wrapper">
                            <label className="field-label field-label-required">{t('Format du contenu')}</label>
                          </div>
                          <div className={`form-autocomplete-wrapper ${!contentFormat ? 'field-required-empty' : ''}`}>
                            <WebsiteFilterAutocomplete
                              disabled={false}
                              list={contentFormatList}
                              value={contentFormat}
                              setValue={(option) => setContentFormat(option)}
                              getOptionLabel={(option) => option?.name || ''}
                              placeholder={t("Sélectionner un format")}
                              style={{
                                maxWidth: 300,
                                width: 300,
                                backgroundImage: contentFormat?.isCustom
                                  ? "url('/Images/perso-icon.png')"
                                  : 'none',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: contentFormat?.isCustom
                                  ? '10px center'
                                  : 'initial',
                                backgroundSize: contentFormat?.isCustom
                                  ? '16px 16px'
                                  : 'initial',
                                paddingLeft: contentFormat?.isCustom ? '32px' : '10px',
                                border: !contentFormat ? '1px solid #8B5CF6' : '1px solid #e0e0e0'
                              }}
                              renderOptionLabel={(option) => {
                                if (option?.isCustom) {
                                  return (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                                      <img src="/Images/perso-icon.png" alt="Personnalisé" style={{ width: '16px', height: '16px' }} />
                                      <span>{option.name}</span>
                                    </div>
                                  );
                                }
                                return option?.name || '';
                              }}
                            />
                          </div>
                        </div>
                        {contentFormat?.isCustom && (
                          <div className="form-field">
                            <div className="field-label-wrapper">
                              <label className="field-label">{t('Nombre de mots par texte')}</label>
                              <div className="field-hint-custom">
                                <span className="hint-custom-label">{t('Format personnalisé')}</span>
                                <span className="hint-custom-text"> - {t('définir le nombre de mots')}</span>
                              </div>
                            </div>
                            <div className="form-input-wrapper word-count-field">
                              <input
                                type="number"
                                className="custom-word-count-input"
                                value={customWordCount}
                                onChange={(e) => setCustomWordCount(e.target.value)}
                                min="1"
                                placeholder="850"
                                required
                              />
                              <span className="word-count-suffix">{t('mots')}</span>
                              <div className="word-count-arrows" aria-hidden="true">
                                <button
                                  type="button"
                                  className="word-count-arrow-btn"
                                  tabIndex={-1}
                                  onClick={() =>
                                    setCustomWordCount((prev) =>
                                      String(Math.max(1, (parseInt(prev, 10) || 0) + 1))
                                    )
                                  }
                                />
                                <button
                                  type="button"
                                  className="word-count-arrow-btn"
                                  tabIndex={-1}
                                  onClick={() =>
                                    setCustomWordCount((prev) =>
                                      String(Math.max(1, (parseInt(prev, 10) || 0) - 1))
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {selectedObjective !== 'traduction' && (
                      <>
                        <h2 className="advanced-options-title">{t('Options avancées')}</h2>
                        <div className="advanced-options-subtitle">
                          <img src="/Images/option-icon.png" alt="Warning" className="advanced-options-warning-icon" />
                          <span className="advanced-options-warning-text">
                            {t('Les options activées consomment des crédits supplémentaires.')}
                          </span>
                        </div>
                        <div className="advanced-options-block">
                          <div className="content-enrichment-card content-enrichment-group">
                            <div className={`content-enrichment-row ${!isEnrichmentAvailable ? 'content-enrichment-row-disabled' : ''}`}>
                              <div className="content-enrichment-left">
                                <label className="content-enrichment-label">
                                  {t('Enrichissement du contenu')}
                                </label>
                              </div>
                              <div className="content-enrichment-toggle-wrapper">
                                <label className="toggle-switch">
                                  <input
                                    type="checkbox"
                                    checked={contentEnrichment}
                                    disabled={!isEnrichmentAvailable}
                                    onChange={(e) => {
                                      setContentEnrichment(e.target.checked);
                                    }}
                                  />
                                  <span className="toggle-slider"></span>
                                </label>
                              </div>
                            </div>

                            <div className={`content-enrichment-row ${!isRagAvailable ? 'content-enrichment-row-disabled' : ''}`}>
                              <div className="content-enrichment-left">
                                <label className="content-enrichment-label">
                                  {t('IA augmentée par vos données')}
                                </label>
                              </div>
                              <div className="content-enrichment-toggle-wrapper">
                                <label className="toggle-switch">
                                  <input
                                    type="checkbox"
                                    checked={ragEnabled}
                                    disabled={!isRagAvailable}
                                    onChange={(e) => {
                                      if (!isRagAvailable) return;
                                      setRagEnabled(e.target.checked);
                                    }}
                                  />
                                  <span className="toggle-slider"></span>
                                </label>
                              </div>
                            </div>

                            {/*
                            <div className="content-enrichment-row image-generation-row">
                              <div className="content-enrichment-left">
                                <label className="content-enrichment-label">
                                  {t("Génération d'image")}
                                </label>

                                {imageGenerationEnabled && (
                                  <div className="image-generation-settings">
                                    <div className="image-generation-settings-left">
                                      <label className="image-generation-sub-label">
                                        {t("Nombre d'images par texte")}
                                      </label>


                                    </div>
                                    <div className="form-input-wrapper">
                                      <input
                                        type="number"
                                        className="custom-word-count-input"
                                        value={imageCount}
                                        onChange={(e) => {
                                          const value = parseInt(e.target.value, 10);
                                          setImageCount(!isNaN(value) ? value : 1);
                                        }}
                                        min="1"
                                        max="10"
                                        placeholder="1"
                                        required
                                      />
                                      <UpDownInputArrows
                                        onIncrease={() =>
                                          setImageCount((prev) => Math.min(10, prev + 1))
                                        }
                                        onDecrease={() =>
                                          setImageCount((prev) => Math.max(1, prev - 1))
                                        }
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>

                              <div className="content-enrichment-toggle-wrapper image-generation-toggle-wrapper">
                                <label className="toggle-switch">
                                  <input
                                    type="checkbox"
                                    checked={imageGenerationEnabled}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setImageGenerationEnabled(checked);
                                    }}
                                  />
                                  <span className="toggle-slider"></span>
                                </label>
                              </div>
                            </div>
                            */}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                ) : productionMode === 'redacteur' ? null : null}
                <div className="step-navigation-buttons writer-step-navigation-buttons">
                  <button className="back-step-button" onClick={() => setCurrentStep(currentStep - 1)}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>{t('Retour')}</span>
                  </button>
                  <button
                    className="next-step-button"
                    disabled={!isStep2Valid()}
                    onClick={() => {
                      if (isStep2Valid()) {
                        setCurrentStep(3);
                      }
                    }}
                  >
                    <span>{t('Suivant')}</span>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 12L10 8L6 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            {currentStep === 3 && productionMode === 'ia-human' && (
              <div>
                <h1 className="project-title">{t('Répartition du travail')}</h1>
                <p className="writer-iahuman-description">
                  {t('Sélectionnez les personnes en charge de la relecture des contenus générés par l\'IA et répartissez')}
                  <br /> {t('les textes à valider.')}
                  <br />
                  {t('Le relecteur peut apporter des corrections et ajustements ciblés dans la limite de mots définie,')}
                  <br /> {t('sans réécrire entièrement le contenu.')}
                </p>
                {writerTasks.length > 0 && (
                  <div
                    style={{
                      marginLeft: '125px',
                      marginBottom: '20px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      maxWidth: '940px',
                    }}
                  >
                    <img
                      src="/Images/icons/verif-icon.png"
                      alt="verification"
                      style={{
                        width: '16px',
                        height: '16px',
                        flexShrink: 0,
                      }}
                    />
                    <span style={{
                      fontSize: '14px',
                      color: "#818181"
                    }}>
                      {!isWriterTextsValid()
                        ? `${getTotalWriterTexts()} /${numberOfTexts} textes répartis`
                        : `${getTotalWriterTexts()} / ${numberOfTexts} textes répartis`}
                    </span>
                  </div>
                )}

                {writerTasks.map((task, index) => (
                  <div
                    className="writer-task-card"
                    key={task.id}
                    style={{
                      marginLeft: '140px',
                      marginRight: 'auto',
                      maxWidth: '940px',
                      padding: '20px 24px',
                      borderRadius: '12px',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0 4px 10px rgba(15, 23, 42, 0.04)',
                      backgroundColor: '#FFFFFF',
                      fontFamily: "'Poppins', sans-serif",
                      marginBottom: index < writerTasks.length - 1 ? '18px' : '0',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '24px',
                        marginBottom: '18px',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '14px',
                            fontWeight: 600,
                            marginBottom: '6px',
                          }}
                        >
                          Relecteur (optionnel)
                        </label>
                        <WebsiteFilterAutocomplete
                          disabled={availableWritersList.length <= 0}
                          list={getAvailableWritersForTask(task.id)}
                          value={task.writer}
                          setValue={(option) => updateWriterTask(task.id, 'writer', option)}
                          getOptionLabel={(option) =>
                            option?.mail ||
                            [option?.firstName, option?.lastName].filter(Boolean).join(' ') ||
                            option?.name ||
                            ''
                          }
                          placeholder={
                            availableWritersList.length === 0
                              ? "Aucun rédacteur disponible"
                              : "Sélectionner un rédacteur"
                          }
                          style={{ width: '100%' }}
                        />
                        {isAllTextsDistributed() && (
                          <div style={{
                            fontSize: '12px',
                            color: '#D97706',
                            marginTop: '4px',
                          }}>
                            <div>{t('Nombre de textes maximum atteint.')}</div>
                            <div>{t('Vous avez réparti tous les textes disponibles pour ce projet.')}</div>
                          </div>
                        )}
                      </div>

                      <div style={{ width: '150px' }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '14px',
                            fontWeight: 500,
                            marginBottom: '6px',
                          }}
                        >
                          Nombre de textes
                        </label>
                        <input
                          type="number"
                          value={task.numberOfTexts || ''}
                          disabled={!task.writer}
                          onChange={(e) => {
                            const value = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                            updateWriterTask(task.id, 'numberOfTexts', value);
                          }}
                          min="1"
                          max={parseInt(numberOfTexts) || 999}
                          required={!!task.writer}
                          style={{
                            width: '100%',
                            height: '40px',
                            borderRadius: '8px',
                            border: task.writer && (!task.numberOfTexts || parseInt(task.numberOfTexts) <= 0) ? '1px solid #8B5CF6' : '1px solid #E5E7EB',
                            padding: '0 12px',
                            fontSize: '14px',
                            color: '#111827',
                            backgroundColor: '#FFFFFF',
                          }}
                        />
                      </div>

                      <div style={{ width: '190px' }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '14px',
                            fontWeight: 500,
                            marginBottom: '6px',
                          }}
                        >
                          Deadline
                        </label>
                        <div
                          className={`date-input-wrapper ${!task.deadline ? 'date-empty' : ''}`}
                          style={{ cursor: task.writer ? 'pointer' : 'not-allowed', width: '100%', maxWidth: '100%', fontSize: '12px' }}
                          onClick={(e) => {
                            if (!task.writer) return;
                            const input = e.currentTarget.querySelector('input[type="date"]');
                            if (input && e.target !== input) {
                              e.preventDefault();
                              input.focus();
                              input.click();
                            }
                          }}
                        >
                          <input
                            type="date"
                            className={`date-input ${task.writer && !task.deadline ? 'field-required-empty' : ''}`}
                            disabled={!task.writer}
                            value={task.deadline}
                            onChange={(e) => updateWriterTask(task.id, 'deadline', e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            required={!!task.writer}
                            style={{
                              fontSize: '12px',
                              color: task.deadline ? '#111827' : 'transparent'
                            }}
                          />
                          {!task.deadline && (
                            <span className="date-placeholder" style={{ fontSize: '12px' }}>{t('Sélectionner une date limite')}</span>
                          )}
                          <div className="date-icon-wrapper" onClick={(e) => {
                            e.stopPropagation();
                            const input = e.currentTarget.parentElement.querySelector('input[type="date"]');
                            if (input) {
                              input.focus();
                              input.showPicker?.();
                            }
                          }}>
                            <img
                              src="/Images/icon-calendar.png"
                              alt="calendar"
                              className="date-icon"
                              style={{ width: '14px', height: '14px' }}
                            />
                          </div>
                        </div>
                      </div>

                      {writerTasks.length > 1 && (
                        <button
                          type="button"
                          aria-label={t("Supprimer la ligne")}
                          onClick={() => removeWriterTask(task.id)}
                          style={{
                            marginLeft: 'auto',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#F1F1F1',
                            borderRadius: '10%',
                            marginTop: '-10px',
                          }}
                        >
                          <img
                            src="Images/icon-trash.png"
                            alt="icon-trash"
                            style={{ width: '25px', height: '25px', opacity: 0.85 }}
                          />
                        </button>
                      )}
                    </div>
                    <hr
                      style={{
                        border: 'none',
                        borderTop: '1px solid #B0B0B0',
                        margin: '16px 0',
                        width: '100%',
                      }}
                    />
                    <div
                      className="relecteur-limit-block"
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '20px',
                      }}
                    >
                      <div>
                        <div className="relecteur-limit-title">
                          {t('Limite de modification par texte')}
                        </div>
                        <div className="relecteur-limit-subtext">
                          {t('Nombre maximum de mots que le relecteur peut modifier ou ajouter.')}
                        </div>
                      </div>
                      <div
                        className="relecteur-max-input-group"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '200px',
                        }}
                      >
                        <div
                          className="relecteur-max-label"
                          style={{
                            padding: '0 12px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            border: '1px solid #E5E7EB',
                            borderRight: 'none',
                            borderRadius: '8px 0 0 8px',
                            fontSize: '12px',
                            color: '#6B7280',
                            backgroundColor: '#F9FAFB',
                          }}
                        >
                          Max
                        </div>
                        <input
                          className="relecteur-max-input"
                          type="number"
                          value={task.modificationLimit || ''}
                          onChange={(e) => {
                            const value = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                            updateWriterTask(task.id, 'modificationLimit', value);
                          }}
                          min="0"
                          style={{
                            width: '100%',
                            height: '40px',
                            borderRadius: '0 8px 8px 0',
                            border: '1px solid #E5E7EB',
                            padding: '0 12px',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#111827',
                            borderLeft: 'none',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  className="add-attribution-button"
                  type="button"
                  onClick={addWriterTask}
                  disabled={isMaxTextsReached()}
                  style={{
                    width: '940px',
                    marginLeft: '140px',
                    marginTop: '18px',
                    padding: '8px 16px',
                    border: '2px dashed #CFCFCF',
                    borderRadius: '12px',
                    backgroundColor: isMaxTextsReached() ? '#F5F5F5' : '#FFFFFF',
                    cursor: isMaxTextsReached() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '13px',
                    fontWeight: 500,
                    color: isMaxTextsReached() ? '#9CA3AF' : '#1A1A1A',
                    transition: 'all 0.2s ease',
                    opacity: isMaxTextsReached() ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!isMaxTextsReached()) {
                      e.target.style.borderColor = '#9CA3AF';
                      e.target.style.backgroundColor = '#F9FAFB';
                      e.target.style.color = '#1A1A1A';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMaxTextsReached()) {
                      e.target.style.borderColor = '#D1D5DB';
                      e.target.style.backgroundColor = '#FFFFFF';
                      e.target.style.color = '#1A1A1A';
                    }
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 3V13M3 8H13"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Ajouter une attribution</span>
                </button>

                <div className="step-navigation-buttons">
                  <button className="back-step-button" onClick={() => setCurrentStep(currentStep - 1)}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>{t('Retour')}</span>
                  </button>
                  <button
                    className="next-step-button"
                    disabled={!isWriterTextsValid()}
                    onClick={() => {
                      if (isWriterTextsValid()) {
                        setCurrentStep(4);
                      }
                    }}
                  >
                    <span>{t('Suivant')}</span>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 12L10 8L6 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            {((currentStep === 3 && productionMode !== 'ia-human') || (currentStep === 4 && productionMode === 'ia-human')) && (
              <div>
                <h1 className="project-title">{t('Contexte éditorial & brief détaillé du projet')}</h1>
                <p className="step-description">{t('Ajouter des informations contextuelles pour guider et enrichir la production des contenus.')}</p>
                <div className="editorial-context-block">
                  <div className="form-fields">
                    <div className="form-field form-field-bottom-border">
                      <div className="field-label-wrapper">
                        <label className="field-label">{t('Gap sémantique')}</label>
                        <span className="field-hint">({t('optionnel')})</span>
                      </div>
                      <div className="drive-input-wrapper">
                        <div className="drive-input-icon">
                          <img src="/Images/drive-icon.png" alt="Google Drive" />
                        </div>
                        <input
                          type="text"
                          className="drive-input"
                          placeholder={t('Lien drive...')}
                          value={semanticGapLink}
                          onChange={(e) => setSemanticGapLink(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="form-field">
                      <div className="field-label-wrapper">
                        <label className="field-label">{t('Brief détaillé')}</label>
                        <span className="field-hint">({t('optionnel')})</span>
                      </div>
                      <div className="drive-input-wrapper">
                        <div className="drive-input-icon">
                          <img src="/Images/drive-icon.png" alt="Google Drive" />
                        </div>
                        <input
                          type="text"
                          className="drive-input"
                          placeholder={t('Lien drive...')}
                          value={detailedBriefLink}
                          onChange={(e) => setDetailedBriefLink(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="step-navigation-buttons">
                  <button className="back-step-button" onClick={() => setCurrentStep(currentStep - 1)}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>{t('Retour')}</span>
                  </button>
                  <button className="launch-project-button" onClick={handleLaunchProject}>
                    <img src="/Images/magic-icon.png" alt="magic" className="magic-icon" />
                    <span>{t('Lancer le projet')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className={`create-project-summary ${summaryCollapsed ? 'collapsed' : ''}`}>
            <div className="summary-header">
              <img
                src={summaryCollapsed ? "/Images/icon-menu/icon-open-menu.png" : "/Images/icon-menu/icone-close-menu.png"}
                alt="toggle summary"
                className="summary-toggle-icon"
                onClick={() => setSummaryCollapsed(!summaryCollapsed)}
              />
              <h3 className="summary-title">{t('Récapitulatif')}</h3>
            </div>
            {!summaryCollapsed && (
              <div className="summary-content">
                <ProjectSummary
                  selectedObjective={selectedObjective}
                  projectName={projectName}
                  siteUrl={siteUrl}
                  selectedWebsite={selectedWebsite}
                  language={language}
                  numberOfTexts={numberOfTexts}
                  projectManager={projectManager}
                  deadline={deadline}
                  productionMode={productionMode}
                  selectedAgent={selectedAgent}
                  selectedWriters={selectedWriters}
                  contentFormat={contentFormat}
                  customWordCount={customWordCount}
                  contentEnrichment={contentEnrichment}
                  semanticGapLink={semanticGapLink}
                  detailedBriefLink={detailedBriefLink}
                  currentStep={currentStep}
                  writerTasks={writerTasks}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <QuitProjectCreationModal
        isOpen={showQuitModal}
        onClose={() => setShowQuitModal(false)}
        onSaveDraft={handleSaveDraft}
        onQuitWithoutSaving={handleQuitWithoutSaving}
      />

      <ProjectCreatedModal
        isOpen={showProjectCreatedModal}
        onClose={handleViewProject}
        onConfirm={handleViewProject}
      />

      <ProjectCreatedModal
        isOpen={showProjectErrorModal}
        onClose={() => {
          setShowProjectErrorModal(false);
          setProjectErrorResponse(null);
        }}
        onConfirm={() => {
          setShowProjectErrorModal(false);
          setProjectErrorResponse(null);
        }}
        isError={true}
        errorResponse={projectErrorResponse}
      />

      <ErrorSuccesModal
        open={showErrorModal}
        message={errorMessage}
        handleClose={() => setShowErrorModal(false)}
        success={false}
        toggle={() => setShowErrorModal(!showErrorModal)}
      />
    </div>
  );
}